import { useEffect, useRef, useState } from 'react';

/**
 * Animates text revealing at a steady character rate, smoothing out
 * large streaming chunks into a consistent trickle.
 */
export function useChatTypewriter(
  target: string,
  charsPerSecond: number,
  animate = true
) {
  const [text, setText] = useState('');
  const r = useRef({
    target,
    length: 0,
    raf: null as number | null,
    lastT: null as number | null,
    cps: charsPerSecond,
  });

  r.current.target = target;
  r.current.cps = charsPerSecond;

  // Reset when target shrinks (new message mounted)
  useEffect(() => {
    if (!animate) {
      return;
    }
    if (target.length < r.current.length) {
      r.current.length = 0;
      setText('');
    }
  }, [target, animate]);

  // Start animation loop when new content arrives
  useEffect(() => {
    if (!animate) {
      return;
    }
    if (r.current.raf !== null || target.length <= r.current.length) {
      return;
    }

    const tick = (t: number) => {
      if (r.current.lastT === null) {
        r.current.lastT = t;
        r.current.raf = requestAnimationFrame(tick);
        return;
      }

      const dt = t - r.current.lastT;
      r.current.lastT = t;

      const add = Math.max(1, Math.round((dt / 1000) * r.current.cps));
      r.current.length = Math.min(
        r.current.target.length,
        r.current.length + add
      );
      setText(r.current.target.slice(0, r.current.length));

      if (r.current.length < r.current.target.length) {
        r.current.raf = requestAnimationFrame(tick);
      } else {
        r.current.raf = null;
        r.current.lastT = null;
      }
    };

    r.current.raf = requestAnimationFrame(tick);
  }, [target, animate]);

  useEffect(() => {
    const ref = r.current;
    return () => {
      if (ref.raf !== null) {
        cancelAnimationFrame(ref.raf);
        ref.raf = null;
        ref.lastT = null;
        ref.length = 0;
      }
    };
  }, []);

  if (!animate) {
    return target;
  }
  return text;
}
